import {
  Archive,
  Bell,
  Boxes,
  Check,
  ChevronRight,
  ChevronsUpDown,
  CircleUserRound,
  Folder,
  FolderOpen,
  History,
  Layers3,
  Lock,
  LogOut,
  MoreHorizontal,
  Plus,
  Search,
  Share2,
  Settings2,
  Table2,
  Plug,
  Trash2,
  Users,
  Zap,
} from "lucide-react";
import { FormEvent, type ReactElement, type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { getEventCoordinates } from "@dnd-kit/utilities";
import { AnimatePresence, motion } from "motion/react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuBadge,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import type { BoardKind, BoardPurpose, BoardStatusCategory, BoardView } from "../../../entities/board/model/use-boards";
import { useBoards } from "../../../entities/board/model/use-boards";
import { useDashboard } from "../../../entities/dashboard/model/use-dashboard";
import type { FolderDefinition } from "../../../entities/folder/model/use-folders";
import { useFolders } from "../../../entities/folder/model/use-folders";
import { SpaceDefinition, useSpaces } from "../../../entities/space/model/use-spaces";
import { treeAccessKey, useTreeAccessSummaries, type TreeAccessSummary } from "../../../entities/access/model/use-tree-access-summaries";
import { useWorkspaces, type WorkspaceMembership } from "../../../entities/workspace/model/use-workspaces";
import { localeNames, locales, useI18n, useT, type Translate } from "../../../shared/i18n/i18n";
import { useSpaceLiveEvents } from "../../../entities/space/model/use-space-live-events";
import { useSignOut } from "../../../features/auth/model/use-sign-out";
import { useTreeReorder } from "../../../features/tree-reorder/model/use-tree-reorder";
import { cn } from "../../../shared/lib/cn";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMenuShortcut, matchesMenuShortcut, type MenuShortcut } from "../../../shared/lib/menu-shortcuts";
import { ApplyBoardTemplateDialog } from "../../../features/boards/apply-template/ui/apply-board-template-dialog";
import { BoardStatusManagerDialog } from "../../../features/board-statuses/manage-statuses/ui/board-status-manager-dialog";
import { BoardActionsMenu } from "../../../features/boards/board-actions/ui/board-actions-menu";
import { BoardSettingsDialog } from "../../../features/boards/manage-board/ui/board-settings-dialog";
import { BoardsPanel } from "../../../widgets/boards/ui/boards-panel";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { WorkObjectFieldsPanel } from "../../../widgets/work-object-fields/ui/work-object-fields-panel";
import { CommandSearch } from "../../../features/search/ui/command-search";
import { InboxSheet } from "../../../features/notifications/ui/inbox-sheet";
import { MyWorkPanel } from "../../../widgets/my-work/ui/my-work-panel";
import { TrashPanel } from "../../../widgets/trash/ui/trash-panel";
import { useNotifications } from "../../../entities/notification/model/use-notifications";
import { WorkObjectTypesPanel } from "../../../widgets/work-object-types/ui/work-object-types-panel";
import { WorkspaceTeamPanel } from "../../../widgets/workspace-team/ui/workspace-team-panel";
import { IntegrationsPanel } from "../../../widgets/integrations/ui/integrations-panel";
import { ActivityPanel } from "../../../widgets/activity/ui/activity-panel";
import { ResourceAccessDialog } from "../../../features/access/manage-resource-access/ui/resource-access-dialog";

type DashboardPageProps = {
  onSignedOut: () => void;
};

type MainMode = "board" | "my-work" | "trash" | "types" | "fields" | "team" | "integrations" | "activity";

type BoardStatusActions = {
  archiveStatus: (input: { boardId: string; boardStatusId: string; replacementStatusId?: string | null }) => Promise<void>;
  clearStatusEntryRule: (input: { boardId: string; targetStatusId: string }) => Promise<void>;
  createStatus: (input: { boardId: string; category: BoardStatusCategory; color: string; name: string }) => Promise<void>;
  moveStatus: (input: { boardId: string; boardStatusId: string; nextStatusId?: string | null; previousStatusId?: string | null }) => Promise<void>;
  setDefaultStatus: (input: { boardId: string; boardStatusId: string }) => Promise<void>;
  updateStatus: (input: { boardId: string; boardStatusId: string; color: string; name: string }) => Promise<void>;
  updateStatusEntryRule: (input: { boardId: string; expression: string; targetStatusId: string }) => Promise<void>;
};

type DashboardRoute = {
  boardId: string;
  mode: MainMode;
  spaceId: string;
  viewId: string;
};

type TreeOrderItem = {
  id: string;
  kind: "board" | "folder";
};

const TreeAccessContext = createContext<{
  byScope: Map<string, TreeAccessSummary>;
  reload: () => Promise<void>;
}>({ byScope: new Map(), reload: async () => {} });

function TreeAccessBadge({ scopeId, scopeType }: { scopeId: string; scopeType: TreeAccessSummary["scopeType"] }) {
  const t = useT();
  const { byScope } = useContext(TreeAccessContext);
  const summary = byScope.get(treeAccessKey(scopeType, scopeId));
  if (!summary) {
    return null;
  }
  if (summary.effectiveMode === "private") {
    return <Lock aria-label={t("space.private")} className="!size-3 shrink-0 !text-sidebar-foreground/45" />;
  }
  if (summary.grantCount > 0) {
    return <Users aria-label={t("space.shared")} className="!size-3 shrink-0 !text-sidebar-foreground/45" />;
  }
  return null;
}

type TreeRenderItem = TreeOrderItem & {
  isDefault?: boolean;
  name: string;
  parentFolderId: string | null;
  rank: string;
  spaceId: string;
};

type TreeVisibleItem = TreeRenderItem & {
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
};

type TreeDropTarget =
  | { kind: "container"; parentFolderId: string | null; spaceId: string }
  | {
      item: TreeOrderItem;
      kind: "item";
      parentFolderId: string | null;
      placement: "after" | "before";
      spaceId: string;
    };

type TreeDropIndicator =
  | { key: string; kind: "inside" }
  | { depthOffset: number; key: string; kind: "line-after" | "line-before" };

type TreeDropResolution = { indicator: TreeDropIndicator; target: TreeDropTarget };

type TreeRowDndState = {
  isDragging: boolean;
  rowProps: Record<string, unknown>;
  setRowRef: (element: HTMLElement | null) => void;
};

const treeMotionTransition = { duration: 0.2, ease: "easeOut" } as const;
const actionMotionTransition = { duration: 0.16, ease: "easeOut" } as const;
const treeBaseRowWidthRem = 13.625;
const treeNestedRowInsetRem = 1.625;
const treeSubMenuClassName = "ml-3.5 mr-0 gap-px pl-2.5 pr-0";
// Matches the per-level inset of treeSubMenuClassName: ml-3.5 (14px) + pl-2.5 (10px).
const treeIndentPx = 24;
const treeAutoExpandDelayMs = 500;

function orderBoardsForNavigation(boards: BoardView[]) {
  return [...boards].sort((first, second) => Number(second.purpose === "default") - Number(first.purpose === "default"));
}

export function DashboardPage({ onSignedOut }: DashboardPageProps) {
  const t = useT();
  const { dashboard, error, reload } = useDashboard();
  const boards = useBoards();
  const folders = useFolders();
  const spaces = useSpaces();
  const treeAccess = useTreeAccessSummaries();
  const workspaces = useWorkspaces();
  const treeAccessValue = useMemo(
    () => ({ byScope: treeAccess.byScope, reload: treeAccess.reload }),
    [treeAccess.byScope, treeAccess.reload],
  );
  const [route, setRoute] = useState(() => parseDashboardRoute(window.location.pathname));
  const [isInboxOpen, setIsInboxOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // Navigating anywhere dismisses the inbox overlay — it must never sit on
  // top of a freshly opened page.
  useEffect(() => {
    setIsInboxOpen(false);
  }, [route]);
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setIsSearchOpen((current) => !current);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
  // ?item=<workObjectId> on a board URL opens that item's dialog (deep links
  // from the inbox, search and shared links).
  const [itemToOpen, setItemToOpen] = useState<{ boardId: string; workObjectId: string } | null>(() => {
    const workObjectId = new URLSearchParams(window.location.search).get("item");
    const initialRoute = parseDashboardRoute(window.location.pathname);
    return workObjectId && initialRoute.boardId ? { boardId: initialRoute.boardId, workObjectId } : null;
  });
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has("item")) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("item");
    window.history.replaceState(null, "", url);
  }, []);
  const [workConfigurationVersion, setWorkConfigurationVersion] = useState(0);
  const signOut = useSignOut({ onSignedOut });
  const treeReorder = useTreeReorder();

  const boardsBySpaceId = useMemo(() => {
    const grouped = new Map<string, BoardView[]>();
    for (const board of boards.data?.boards ?? []) {
      grouped.set(board.spaceId, orderBoardsForNavigation([...(grouped.get(board.spaceId) ?? []), board]));
    }
    return grouped;
  }, [boards.data?.boards]);

  const foldersBySpaceId = useMemo(() => {
    const grouped = new Map<string, FolderDefinition[]>();
    for (const folder of folders.data) {
      grouped.set(folder.spaceId, [...(grouped.get(folder.spaceId) ?? []), folder]);
    }
    return grouped;
  }, [folders.data]);

  const resolvedSelection = useMemo(() => {
    const allBoards = boards.data?.boards ?? [];
    const routeSpace = spaces.data.find((space) => space.id === route.spaceId) ?? null;
    const fallbackSpace = spaces.data.find((space) => space.isDefault) ?? spaces.data[0] ?? null;
    const selectedSpace = routeSpace ?? fallbackSpace;
    const boardsForSelectedSpace = selectedSpace ? boardsBySpaceId.get(selectedSpace.id) ?? [] : [];
    const selectedBoard =
      boardsForSelectedSpace.find((board) => board.id === route.boardId) ??
      (route.mode === "board" ? boardsForSelectedSpace[0] ?? null : null);

    return {
      allBoards,
      boardsForSelectedSpace,
      selectedBoard,
      selectedBoardId: selectedBoard?.id ?? "",
      selectedSpace,
      selectedSpaceId: selectedSpace?.id ?? "",
    };
  }, [boards.data?.boards, boardsBySpaceId, route.boardId, route.mode, route.spaceId, spaces.data]);

  const { selectedBoard, selectedBoardId, selectedSpace, selectedSpaceId } = resolvedSelection;
  const mode = route.mode;

  useEffect(() => {
    function handlePopState() {
      setRoute(parseDashboardRoute(window.location.pathname));
    }

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (spaces.isLoading || boards.isLoading) {
      return;
    }

    const nextRoute = normalizeDashboardRoute(route, resolvedSelection);
    if (dashboardPath(nextRoute) !== window.location.pathname) {
      navigateDashboard(nextRoute, { replace: true });
    }
  }, [boards.isLoading, resolvedSelection, route, spaces.isLoading]);

  const handleTreeInvalidated = useCallback(() => {
    void Promise.all([boards.reload(), folders.reload()]);
  }, [boards.reload, folders.reload]);

  const handleTreeReordered = useCallback((event: {
    clientMutationId?: string;
    payload: { items: Array<{ id: string; kind: "board" | "folder"; rank: string }>; parentFolderId: string | null };
  }) => {
    boards.applyTreeOrder(event.payload);
    folders.applyTreeOrder(event.payload);
  }, [boards.applyTreeOrder, folders.applyTreeOrder]);

  useSpaceLiveEvents({
    onBoardItemGroupChanged: boards.applyBoardItemGroupPatch,
    onBoardViewOrderChanged: boards.applyBoardViewOrderPatch,
    onBoardUpdated: boards.applyBoardPatch,
    onFolderUpdated: folders.applyFolderPatch,
    onSpaceUpdated: spaces.applySpacePatch,
    onTreeInvalidated: handleTreeInvalidated,
    onTreeReordered: handleTreeReordered,
    spaceId: selectedSpaceId,
  });

  function navigateDashboard(nextRoute: DashboardRoute, options?: { replace?: boolean }) {
    const nextPath = dashboardPath(nextRoute);
    if (nextPath !== window.location.pathname) {
      window.history[options?.replace ? "replaceState" : "pushState"](null, "", nextPath);
    }
    setRoute(parseDashboardRoute(nextPath));
  }

  // Jump to an item from anywhere (inbox, search): navigate to its board and
  // hand the open request to the board panel.
  function openItemFromAnywhere(target: { boardId: string; workObjectId: string }) {
    const board = boards.data?.boards.find((candidate) => candidate.id === target.boardId);
    if (!board) return;
    navigateDashboard({ boardId: board.id, mode: "board", spaceId: board.spaceId, viewId: "" });
    setItemToOpen(target);
  }

  async function refreshWorkConfiguration() {
    setWorkConfigurationVersion((version) => version + 1);
    await Promise.all([reload(), boards.reload(), folders.reload(), spaces.reload(), treeAccess.reload()]);
  }

  async function switchWorkspace(workspaceId: string) {
    await workspaces.switchWorkspace(workspaceId);
    // All loaded data (spaces, boards, live subscriptions, route ids) belongs
    // to the previous workspace — a full reload from the root is the reset.
    window.location.assign("/");
  }

  async function createWorkspace(name: string) {
    await workspaces.createWorkspace(name);
    window.location.assign("/");
  }

  if (error) {
    return (
      <main className="grid min-h-dvh place-items-center px-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("nav.couldNotLoad")}</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={signOut} variant="outline">
              {t("nav.signOut")}
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (!dashboard) {
    return (
      <main className="grid min-h-dvh place-items-center px-6">
        <Skeleton className="h-24 w-full max-w-md" />
      </main>
    );
  }

  return (
    <TreeAccessContext.Provider value={treeAccessValue}>
    <SidebarProvider>
      <a
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:text-primary-foreground"
        href="#main-content"
      >
        {t("nav.skipToContent")}
      </a>
      <WorkspaceSidebar
        boardsBySpaceId={boardsBySpaceId}
        dashboardStats={dashboard.stats}
        isCreatingBoard={boards.isMutating}
        isCreatingFolder={folders.isCreating}
        isCreatingSpace={spaces.isCreating}
        isLoading={boards.isLoading || folders.isLoading || spaces.isLoading}
        mode={mode}
        onOpenInbox={() => setIsInboxOpen(true)}
        onOpenSearch={() => setIsSearchOpen(true)}
        boardStatusActions={{
          archiveStatus: boards.archiveStatus,
          clearStatusEntryRule: boards.clearStatusEntryRule,
          createStatus: boards.createStatus,
          moveStatus: boards.moveStatus,
          setDefaultStatus: boards.setDefaultStatus,
          updateStatus: boards.updateStatus,
          updateStatusEntryRule: boards.updateStatusEntryRule,
        }}
        onArchiveBoard={async (input) => {
          const nextData = await boards.archiveBoard(input);
          if (selectedBoardId === input.boardId) {
            const nextBoard = nextData.boards.find((board) => board.spaceId === selectedSpaceId) ?? null;
            navigateDashboard({ boardId: nextBoard?.id ?? "", mode: "board", spaceId: selectedSpaceId, viewId: "" });
          }
        }}
        onArchiveFolder={async (input) => {
          const folderIds = collectFolderSubtreeIds(folders.data, input.folderId);
          const archivedBoardIds = new Set(
            (boards.data?.boards ?? [])
              .filter((board) => board.folderId && folderIds.has(board.folderId))
              .map((board) => board.id),
          );
          await folders.archiveFolder(input);
          if (archivedBoardIds.has(selectedBoardId)) {
            const nextBoard = (boards.data?.boards ?? []).find(
              (board) => board.spaceId === selectedSpaceId && !archivedBoardIds.has(board.id),
            ) ?? null;
            navigateDashboard({ boardId: nextBoard?.id ?? "", mode: "board", spaceId: selectedSpaceId, viewId: "" });
          }
          await boards.reload();
        }}
        onCreateBoard={async (input) => {
          await boards.createBoard(input);
        }}
        onCreateFolder={async (input) => {
          await folders.createFolder(input);
        }}
        onCreateSpace={async (input) => {
          const created = await spaces.createSpace(input);
          navigateDashboard({ boardId: "", mode: "board", spaceId: created.id, viewId: "" });
          await refreshWorkConfiguration();
        }}
        onMoveBoard={async (input) => {
          await boards.moveBoard(input);
        }}
        onMoveFolder={async (input) => {
          await folders.moveFolder(input);
        }}
        onReorderTree={async (input) => {
          boards.applyTreeOrder(input);
          folders.applyTreeOrder(input);
          try {
            await treeReorder.reorderTree(input);
          } catch (err) {
            await Promise.all([boards.reload(), folders.reload()]);
            throw err;
          }
        }}
        onUpdateBoard={async (input) => {
          await boards.updateBoard(input);
          await refreshWorkConfiguration();
        }}
        onUpdateFolder={async (input) => {
          await folders.updateFolder(input);
          await refreshWorkConfiguration();
        }}
        onUpdateSpace={async (input) => {
          await spaces.updateSpace(input);
          await refreshWorkConfiguration();
        }}
        onSelectBoard={(board) => {
          navigateDashboard({ boardId: board.id, mode: "board", spaceId: board.spaceId, viewId: "" });
        }}
        onSelectMode={(nextMode) => {
          navigateDashboard({
            boardId: nextMode === "board" ? selectedBoardId : "",
            mode: nextMode,
            spaceId: nextMode === "board" ? selectedSpaceId : "",
            viewId: nextMode === "board" ? route.viewId : "",
          });
        }}
        onSelectSpace={(spaceId) => {
          const nextBoard = boardsBySpaceId.get(spaceId)?.[0] ?? null;
          navigateDashboard({ boardId: nextBoard?.id ?? "", mode: "board", spaceId, viewId: "" });
        }}
        onCreateWorkspace={createWorkspace}
        onSignOut={signOut}
        onSwitchWorkspace={switchWorkspace}
        selectedBoardId={selectedBoardId}
        selectedSpaceId={selectedSpaceId}
        foldersBySpaceId={foldersBySpaceId}
        spaces={spaces.data}
        workspaceName={dashboard.workspace?.name ?? "Workspace"}
        workspaces={workspaces.data}
      />

      <SidebarInset>
        {mode !== "board" ? (
          <header className="sticky top-0 z-10 pt-2 pr-2 pb-0 pl-0">
            <div className="inline-flex max-w-full flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 bg-card/95 px-2.5 py-1.5 shadow-sm backdrop-blur">
              <div className="flex min-w-0 items-center gap-3">
                <SidebarTrigger />
                <div className="min-w-0 py-1">
                  <DashboardBreadcrumbs
                    boardName={selectedBoard?.name ?? null}
                    mode={mode}
                    spaceName={selectedSpace?.name ?? dashboard.workspace?.name ?? "Workspace"}
                    workspaceName={dashboard.workspace?.name ?? "Workspace"}
                  />
                </div>
              </div>
            </div>
          </header>
        ) : null}

        <div
          id="main-content"
          className={cn(
            "flex min-h-0 w-full min-w-0 flex-1 flex-col gap-3 pt-2 pr-2 pb-2 pl-0",
            mode === "board" && "h-svh min-h-0 overflow-hidden",
          )}
        >
          {mode === "board" && selectedBoardId ? (
            <BoardsPanel
              key={`${selectedBoardId}:${workConfigurationVersion}`}
              navigationSlot={
                <div className="flex min-w-0 items-center gap-2 pt-0.5">
                  <SidebarTrigger />
                  <DashboardBreadcrumbs
                    boardName={selectedBoard?.name ?? null}
                    hideCurrent
                    mode={mode}
                    spaceName={selectedSpace?.name ?? dashboard.workspace?.name ?? "Workspace"}
                    workspaceName={dashboard.workspace?.name ?? "Workspace"}
                  />
                </div>
              }
              onBoardChanged={refreshWorkConfiguration}
              onOpenItemRequestHandled={() => setItemToOpen(null)}
              onSelectViewChange={(viewId) => {
                navigateDashboard({ boardId: selectedBoardId, mode: "board", spaceId: selectedSpaceId, viewId });
              }}
              openItemRequest={itemToOpen}
              selectedBoardId={selectedBoardId}
              selectedViewId={route.viewId}
              showBoardList={false}
              showCreateBoardForm
            />
          ) : null}
          {mode === "types" ? <WorkObjectTypesPanel onChanged={refreshWorkConfiguration} /> : null}
          {mode === "fields" ? <WorkObjectFieldsPanel /> : null}
          {mode === "team" ? <WorkspaceTeamPanel /> : null}
          {mode === "integrations" ? <IntegrationsPanel /> : null}
          {mode === "activity" ? <ActivityPanel /> : null}
          {mode === "trash" ? <TrashPanel /> : null}
          {mode === "my-work" ? <MyWorkPanel onOpenItem={openItemFromAnywhere} /> : null}
        </div>
      </SidebarInset>

      <InboxSheet onOpenChange={setIsInboxOpen} onOpenNotification={openItemFromAnywhere} open={isInboxOpen} />
      <CommandSearch onOpenChange={setIsSearchOpen} onOpenItem={openItemFromAnywhere} open={isSearchOpen} />
    </SidebarProvider>
    </TreeAccessContext.Provider>
  );
}

// Kept as its own component so only the badge subscribes to the unread poll.
function SidebarInboxButton({ onOpenInbox }: { onOpenInbox: () => void }) {
  const t = useT();
  const notifications = useNotifications({ listEnabled: false });
  return (
    <SidebarMenuButton onClick={onOpenInbox} size="sm">
      <Bell />
      <span>{t("nav.inbox")}</span>
      {notifications.unreadCount > 0 ? <SidebarMenuBadge className="bg-primary/15 text-primary">{notifications.unreadCount}</SidebarMenuBadge> : null}
    </SidebarMenuButton>
  );
}

function DashboardBreadcrumbs({
  boardName,
  hideCurrent = false,
  mode,
  spaceName,
  workspaceName,
}: {
  boardName: string | null;
  hideCurrent?: boolean;
  mode: MainMode;
  spaceName: string;
  workspaceName: string;
}) {
  const t = useT();
  const sectionName = mode === "board" ? null : mainModeTitle(mode, t);
  const rootName = sectionName ? workspaceName : spaceName;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          {sectionName || (boardName && !hideCurrent) ? (
            <span className="max-w-64 truncate">{rootName}</span>
          ) : (
            <BreadcrumbPage className="max-w-64 truncate">{rootName}</BreadcrumbPage>
          )}
        </BreadcrumbItem>
        {boardName ? (
          <>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            {!hideCurrent ? (
              <BreadcrumbItem>
                <BreadcrumbPage className="max-w-64 truncate">{boardName}</BreadcrumbPage>
              </BreadcrumbItem>
            ) : null}
          </>
        ) : null}
        {sectionName ? (
          <>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>{sectionName}</BreadcrumbPage>
            </BreadcrumbItem>
          </>
        ) : null}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function WorkspaceSidebar({
  boardStatusActions,
  boardsBySpaceId,
  dashboardStats,
  foldersBySpaceId,
  isCreatingBoard,
  isCreatingFolder,
  isCreatingSpace,
  isLoading,
  mode,
  onArchiveBoard,
  onArchiveFolder,
  onCreateBoard,
  onCreateFolder,
  onCreateSpace,
  onMoveBoard,
  onMoveFolder,
  onOpenInbox,
  onOpenSearch,
  onReorderTree,
  onSelectBoard,
  onCreateWorkspace,
  onSelectMode,
  onSelectSpace,
  onSignOut,
  onSwitchWorkspace,
  onUpdateBoard,
  onUpdateFolder,
  onUpdateSpace,
  selectedBoardId,
  selectedSpaceId,
  spaces,
  workspaceName,
  workspaces,
}: {
  boardStatusActions: BoardStatusActions;
  boardsBySpaceId: Map<string, BoardView[]>;
  dashboardStats: { boards: number; spaces: number; workObjects: number; workTypes: number };
  foldersBySpaceId: Map<string, FolderDefinition[]>;
  isCreatingBoard: boolean;
  isCreatingFolder: boolean;
  isCreatingSpace: boolean;
  isLoading: boolean;
  mode: MainMode;
  onArchiveBoard: (input: { boardId: string }) => Promise<void>;
  onArchiveFolder: (input: { folderId: string }) => Promise<void>;
  onCreateBoard: (input: {
    description: string;
    folderId?: string | null;
    kind?: BoardKind;
    name: string;
    purpose: BoardPurpose;
    spaceId: string;
  }) => Promise<void>;
  onCreateFolder: (input: { description: string; name: string; parentFolderId?: string | null; spaceId: string }) => Promise<void>;
  onCreateSpace: (input: { description: string; name: string }) => Promise<void>;
  onMoveBoard: (input: { boardId: string; folderId?: string | null }) => Promise<void>;
  onMoveFolder: (input: { folderId: string; parentFolderId?: string | null }) => Promise<void>;
  onOpenInbox: () => void;
  onOpenSearch: () => void;
  onReorderTree: (input: { items: TreeOrderItem[]; parentFolderId?: string | null; spaceId: string }) => Promise<void>;
  onSelectBoard: (board: BoardView) => void;
  onSelectMode: (mode: MainMode) => void;
  onSelectSpace: (spaceId: string) => void;
  onCreateWorkspace: (name: string) => Promise<void>;
  onSignOut: () => void;
  onSwitchWorkspace: (workspaceId: string) => Promise<void>;
  onUpdateBoard: (input: { boardId: string; name: string }) => Promise<void>;
  onUpdateFolder: (input: { folderId: string; name: string }) => Promise<void>;
  onUpdateSpace: (input: { name: string; spaceId: string }) => Promise<void>;
  selectedBoardId: string;
  selectedSpaceId: string;
  spaces: SpaceDefinition[];
  workspaceName: string;
  workspaces: WorkspaceMembership[];
}) {
  const { locale, setLocale } = useI18n();
  const t = useT();
  const [isCreateSpaceOpen, setIsCreateSpaceOpen] = useState(false);
  const [isCreateWorkspaceOpen, setIsCreateWorkspaceOpen] = useState(false);
  const [activeTreeDrag, setActiveTreeDrag] = useState<TreeOrderItem | null>(null);
  const [collapsedSpaceIds, setCollapsedSpaceIds] = useState<Set<string>>(new Set());
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set());
  const [treeDropResolution, setTreeDropResolution] = useState<TreeDropResolution | null>(null);
  const treeDropResolutionRef = useRef<TreeDropResolution | null>(null);
  const treeAutoExpandRef = useRef<{ folderId: string; timer: number } | null>(null);
  const treeSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  async function createFolderFromTree(spaceId: string, parentFolderId: string | null, name: string) {
    await onCreateFolder({ description: "", name, parentFolderId, spaceId });
    if (parentFolderId) {
      setExpandedFolderIds((current) => new Set(current).add(parentFolderId));
    }
  }

  async function createBoardFromTree(spaceId: string, folderId: string | null, name: string, kind: BoardKind) {
    await onCreateBoard({ description: "", folderId, kind, name, purpose: "custom", spaceId });
  }

  function updateTreeResolution(resolution: TreeDropResolution | null) {
    treeDropResolutionRef.current = resolution;
    setTreeDropResolution((current) => (isSameTreeResolution(current, resolution) ? current : resolution));
  }

  function scheduleTreeAutoExpand(resolution: TreeDropResolution | null) {
    const folderId = resolution?.target.kind === "container" ? resolution.target.parentFolderId : null;
    const pending = treeAutoExpandRef.current;
    if (pending && pending.folderId !== folderId) {
      window.clearTimeout(pending.timer);
      treeAutoExpandRef.current = null;
    }
    if (!folderId || expandedFolderIds.has(folderId) || treeAutoExpandRef.current) {
      return;
    }
    treeAutoExpandRef.current = {
      folderId,
      timer: window.setTimeout(() => {
        treeAutoExpandRef.current = null;
        setExpandedFolderIds((current) => new Set(current).add(folderId));
      }, treeAutoExpandDelayMs),
    };
  }

  function resetTreeDrag() {
    if (treeAutoExpandRef.current) {
      window.clearTimeout(treeAutoExpandRef.current.timer);
      treeAutoExpandRef.current = null;
    }
    updateTreeResolution(null);
    setActiveTreeDrag(null);
  }

  function handleTreeDragStart(event: DragStartEvent) {
    const item = treeItemFromDndData(event.active.data.current);
    setActiveTreeDrag(item ? { id: item.id, kind: item.kind } : null);
  }

  function handleTreeDragMove(event: DragMoveEvent) {
    const active = treeItemFromDndData(event.active.data.current);
    if (!active) {
      return;
    }
    const overData = event.over?.data.current;
    const overRect = event.over?.rect;
    // Keep the previous resolution while the pointer crosses row gaps so the
    // indicator never flickers; the drop always matches what is shown.
    if (!overData || !overRect) {
      return;
    }
    const activationCoordinates = getEventCoordinates(event.activatorEvent);
    if (!activationCoordinates) {
      return;
    }
    const pointer = {
      x: activationCoordinates.x + event.delta.x,
      y: activationCoordinates.y + event.delta.y,
    };
    const spaceBoards = boardsBySpaceId.get(active.spaceId) ?? [];
    const spaceFolders = foldersBySpaceId.get(active.spaceId) ?? [];
    const visibleItems = treeItemsVisibleDuringDrag({
      activeTreeDrag: { id: active.id, kind: active.kind },
      folders: spaceFolders,
      items: visibleTreeItems({ boards: spaceBoards, expandedFolderIds, folders: spaceFolders }),
    });
    const resolution = resolveTreeDrop({
      active,
      overData,
      overRect,
      pointer,
      visibleItems,
    });
    updateTreeResolution(resolution);
    scheduleTreeAutoExpand(resolution);
  }

  async function handleTreeDragEnd(event: DragEndEvent) {
    const active = treeItemFromDndData(event.active.data.current);
    const resolution = treeDropResolutionRef.current;
    resetTreeDrag();
    if (!active || !resolution || !event.over) {
      return;
    }
    await performTreeDrop({ id: active.id, kind: active.kind }, resolution.target);
  }

  async function performTreeDrop(active: TreeOrderItem, target: TreeDropTarget) {
    const spaceBoards = boardsBySpaceId.get(target.spaceId) ?? [];
    const spaceFolders = foldersBySpaceId.get(target.spaceId) ?? [];
    if (isSameTreeItem(target, active)) {
      return;
    }

    if (active.kind === "board") {
      const board = spaceBoards.find((item) => item.id === active.id);
      if (!board || board.spaceId !== target.spaceId) {
        return;
      }

      await onReorderTree({
        items: nextTreeOrder({
          active,
          boards: spaceBoards,
          folders: spaceFolders,
          over: target,
        }),
        parentFolderId: target.parentFolderId,
        spaceId: target.spaceId,
      });
      onSelectBoard({ ...board, folderId: target.parentFolderId });
      return;
    }

    const folder = spaceFolders.find((item) => item.id === active.id);
    if (!folder || folder.spaceId !== target.spaceId) {
      return;
    }
    if (target.parentFolderId && collectFolderSubtreeIds(spaceFolders, folder.id).has(target.parentFolderId)) {
      return;
    }

    await onReorderTree({
      items: nextTreeOrder({
        active,
        boards: spaceBoards,
        folders: spaceFolders,
        over: target,
      }),
      parentFolderId: target.parentFolderId,
      spaceId: target.spaceId,
    });
    setExpandedFolderIds((current) => {
      const next = new Set(current);
      if (target.parentFolderId) {
        next.add(target.parentFolderId);
      }
      return next;
    });
  }

  return (
    <Sidebar aria-label={t("nav.workspaceNavigation")} variant="floating">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <SidebarMenuButton className="h-9 gap-2">
                    <WorkspaceMark className="size-6 rounded-md text-xs" name={workspaceName} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{workspaceName}</span>
                      <span className="block truncate text-xs font-normal text-sidebar-foreground/55">
                        {t("board.spacesAndBoards", {
                          boards: t("table.boardsCount", { count: dashboardStats.boards }),
                          spaces: t("table.spacesCount", { count: dashboardStats.spaces }),
                        })}
                      </span>
                    </span>
                    <ChevronsUpDown className="size-3.5 text-sidebar-foreground/40" />
                  </SidebarMenuButton>
                }
              />
              <DropdownMenuContent align="start" className="min-w-56 p-1.5">
                <DropdownMenuGroup>
                  <DropdownMenuLabel>{t("nav.workspaces")}</DropdownMenuLabel>
                  {workspaces.map((workspace) => (
                    <DropdownMenuItem
                      className="gap-2.5 py-1.5"
                      key={workspace.id}
                      onClick={() => {
                        if (!workspace.isActive) {
                          void onSwitchWorkspace(workspace.id);
                        }
                      }}
                    >
                      <WorkspaceMark className="size-6 rounded-md text-[11px]" name={workspace.name} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{workspace.name}</span>
                        <span className="block truncate text-[11px] capitalize text-muted-foreground">{workspace.role}</span>
                      </span>
                      {workspace.isActive ? <Check className="shrink-0 text-primary" /> : null}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem className="gap-2.5 py-1.5" onClick={() => setIsCreateWorkspaceOpen(true)}>
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-sidebar-accent text-muted-foreground">
                      <Plus />
                    </span>
                    {t("nav.createWorkspace")}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuLabel>{t("nav.language")}</DropdownMenuLabel>
                  {locales.map((candidate) => (
                    <DropdownMenuItem className="gap-2.5 py-1.5" key={candidate} onClick={() => setLocale(candidate)}>
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-sidebar-accent text-[10px] uppercase text-muted-foreground">
                        {candidate}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{localeNames[candidate]}</span>
                      {candidate === locale ? <Check className="shrink-0 text-primary" /> : null}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarInboxButton onOpenInbox={onOpenInbox} />
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton isActive={mode === "my-work"} onClick={() => onSelectMode("my-work")} size="sm">
                  <CircleUserRound />
                  <span>{t("nav.myWork")}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton onClick={onOpenSearch} size="sm">
                  <Search />
                  <span>{t("nav.search")}</span>
                  <SidebarMenuBadge className="text-[10px] text-muted-foreground/70">⌘K</SidebarMenuBadge>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel className="h-6 px-2 text-[11px]">{t("nav.spaces")}</SidebarGroupLabel>
          <SidebarGroupAction aria-label={t("nav.createSpace")} className="top-1.5" onClick={() => setIsCreateSpaceOpen(true)}>
            <Plus />
          </SidebarGroupAction>
          <SidebarGroupContent>
            {isLoading ? <SidebarMenuSkeleton className="h-7" showIcon /> : null}
            <SidebarMenu>
              {spaces.map((space) => {
                const spaceBoards = boardsBySpaceId.get(space.id) ?? [];
                const spaceFolders = foldersBySpaceId.get(space.id) ?? [];
                const isSelectedSpace = selectedSpaceId === space.id;
                const content = (
                  <SpaceSidebarItem
                    boards={spaceBoards}
                    activeTreeDrag={activeTreeDrag}
                    expandedFolderIds={expandedFolderIds}
                    folders={spaceFolders}
                    isCollapsed={collapsedSpaceIds.has(space.id)}
                    isSelected={isSelectedSpace}
                    key={space.id}
                    mode={mode}
                    boardStatusActions={boardStatusActions}
                    onArchiveBoard={onArchiveBoard}
                    onArchiveFolder={onArchiveFolder}
                    onCreateBoard={createBoardFromTree}
                    onCreateFolder={createFolderFromTree}
                    onSelectBoard={onSelectBoard}
                    onSelectSpace={onSelectSpace}
                    onToggleFolder={(folderId) =>
                      setExpandedFolderIds((current) => {
                        const next = new Set(current);
                        if (next.has(folderId)) {
                          next.delete(folderId);
                        } else {
                          next.add(folderId);
                        }
                        return next;
                      })
                    }
                    onToggleSpace={(spaceId) =>
                      setCollapsedSpaceIds((current) => {
                        const next = new Set(current);
                        if (next.has(spaceId)) {
                          next.delete(spaceId);
                        } else {
                          next.add(spaceId);
                        }
                        return next;
                      })
                    }
                    onUpdateBoard={onUpdateBoard}
                    onUpdateFolder={onUpdateFolder}
                    onUpdateSpace={onUpdateSpace}
                    selectedBoardId={selectedBoardId}
                    space={space}
                    treeDropIndicator={isSelectedSpace ? treeDropResolution?.indicator ?? null : null}
                  />
                );

                const activeDragItem = activeTreeDrag
                  ? treeVisibleItemById({ boards: spaceBoards, expandedFolderIds, folders: spaceFolders, item: activeTreeDrag })
                  : null;

                return isSelectedSpace ? (
                  <DndContext
                    collisionDetection={treeCollisionDetection}
                    key={space.id}
                    onDragCancel={resetTreeDrag}
                    onDragEnd={(event) => void handleTreeDragEnd(event)}
                    onDragMove={handleTreeDragMove}
                    onDragStart={handleTreeDragStart}
                    sensors={treeSensors}
                  >
                    {content}
                    <DragOverlay dropAnimation={null}>
                      {activeDragItem ? <TreeDragClone item={activeDragItem} /> : null}
                    </DragOverlay>
                  </DndContext>
                ) : (
                  content
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="h-6 px-2 text-[11px]">{t("nav.workspace")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <WorkspaceModeItem active={mode === "types"} icon={Boxes} label={t("nav.types")} onClick={() => onSelectMode("types")} />
              <WorkspaceModeItem active={mode === "fields"} icon={Settings2} label={t("nav.fields")} onClick={() => onSelectMode("fields")} />
              <WorkspaceModeItem active={mode === "team"} icon={Users} label={t("nav.team")} onClick={() => onSelectMode("team")} />
              <WorkspaceModeItem active={mode === "integrations"} icon={Plug} label={t("nav.integrations")} onClick={() => onSelectMode("integrations")} />
              <WorkspaceModeItem active={mode === "activity"} icon={History} label={t("nav.activity")} onClick={() => onSelectMode("activity")} />
              <WorkspaceModeItem active={mode === "trash"} icon={Trash2} label={t("nav.trash")} onClick={() => onSelectMode("trash")} />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={onSignOut} size="sm">
              <LogOut />
              <span>{t("nav.signOut")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <CreateSpaceDialog
        disabled={isCreatingSpace}
        onCreate={onCreateSpace}
        onOpenChange={setIsCreateSpaceOpen}
        open={isCreateSpaceOpen}
      />

      <CreateWorkspaceDialog
        onCreate={onCreateWorkspace}
        onOpenChange={setIsCreateWorkspaceOpen}
        open={isCreateWorkspaceOpen}
      />
    </Sidebar>
  );
}

function SpaceSidebarItem({
  activeTreeDrag,
  boardStatusActions,
  boards,
  expandedFolderIds,
  folders,
  isCollapsed,
  isSelected,
  mode,
  onArchiveBoard,
  onArchiveFolder,
  onCreateBoard,
  onCreateFolder,
  onSelectBoard,
  onSelectSpace,
  onToggleFolder,
  onToggleSpace,
  onUpdateBoard,
  onUpdateFolder,
  onUpdateSpace,
  selectedBoardId,
  space,
  treeDropIndicator,
}: {
  activeTreeDrag: TreeOrderItem | null;
  boardStatusActions: BoardStatusActions;
  boards: BoardView[];
  expandedFolderIds: Set<string>;
  folders: FolderDefinition[];
  isCollapsed: boolean;
  isSelected: boolean;
  mode: MainMode;
  onArchiveBoard: (input: { boardId: string }) => Promise<void>;
  onArchiveFolder: (input: { folderId: string }) => Promise<void>;
  onCreateBoard: (spaceId: string, folderId: string | null, name: string, kind: BoardKind) => Promise<void>;
  onCreateFolder: (spaceId: string, parentFolderId: string | null, name: string) => Promise<void>;
  onSelectBoard: (board: BoardView) => void;
  onSelectSpace: (spaceId: string) => void;
  onToggleFolder: (folderId: string) => void;
  onToggleSpace: (spaceId: string) => void;
  onUpdateBoard: (input: { boardId: string; name: string }) => Promise<void>;
  onUpdateFolder: (input: { folderId: string; name: string }) => Promise<void>;
  onUpdateSpace: (input: { name: string; spaceId: string }) => Promise<void>;
  selectedBoardId: string;
  space: SpaceDefinition;
  treeDropIndicator: TreeDropIndicator | null;
}) {
  const t = useT();
  const [isSpacePermissionsOpen, setIsSpacePermissionsOpen] = useState(false);
  const treeAccess = useContext(TreeAccessContext);
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={isSelected}
        render={
          <div
            onClick={() => onSelectSpace(space.id)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelectSpace(space.id);
              }
            }}
            role="button"
            tabIndex={0}
          />
        }
        size="sm"
      >
        <button
          aria-label={isCollapsed ? `Expand ${space.name}` : `Collapse ${space.name}`}
          className="-ml-1 flex size-5 items-center justify-center rounded-sm hover:bg-sidebar-accent"
          onClick={(event) => {
            event.stopPropagation();
            onToggleSpace(space.id);
          }}
          type="button"
        >
          <ChevronRight className={cn("transition-transform", !isCollapsed && "rotate-90")} />
        </button>
        <EditableTreeLabel className="min-w-0 flex-1" value={space.name} onCommit={(name) => onUpdateSpace({ name, spaceId: space.id })} />
        <TreeAccessBadge scopeId={space.id} scopeType="space" />
        <button
          aria-label={t("common.accessShareFor", { name: space.name })}
          className="grid size-6 place-items-center rounded-sm text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground"
          onClick={(event) => { event.stopPropagation(); setIsSpacePermissionsOpen(true); }}
          type="button"
        >
          <Share2 className="size-3.5" />
        </button>
      </SidebarMenuButton>
      <ResourceAccessDialog
        name={space.name}
        onOpenChange={(open) => {
          setIsSpacePermissionsOpen(open);
          if (!open) void treeAccess.reload();
        }}
        open={isSpacePermissionsOpen}
        scope={{ id: space.id, type: "space" }}
      />
      {isSelected ? (
        <>
          <CreateTreeMenu
            ariaLabel={t("a11y.createIn", { name: space.name })}
            hasSprintBoard={boards.some((board) => board.kind === "sprint")}
            onCreateBoard={(name, kind) => onCreateBoard(space.id, null, name, kind)}
            onCreateFolder={(name) => onCreateFolder(space.id, null, name)}
            trigger="sidebar-action"
          />
          <AnimatePresence initial={false}>
            {!isCollapsed ? (
              <motion.div
                animate={{ height: "auto", opacity: 1 }}
                className="overflow-hidden"
                exit={{ height: 0, opacity: 0 }}
                initial={{ height: 0, opacity: 0 }}
                key="space-tree"
                transition={treeMotionTransition}
              >
                <SidebarMenuSub className={treeSubMenuClassName}>
                  <SpaceTree
                    activeTreeDrag={activeTreeDrag}
                    boardStatusActions={boardStatusActions}
                    boards={boards}
                    expandedFolderIds={expandedFolderIds}
                    folders={folders}
                    mode={mode}
                    onArchiveBoard={onArchiveBoard}
                    onArchiveFolder={onArchiveFolder}
                    onCreateBoard={onCreateBoard}
                    onCreateFolder={onCreateFolder}
                    onSelectBoard={onSelectBoard}
                    onToggleFolder={onToggleFolder}
                    onUpdateBoard={onUpdateBoard}
                    onUpdateFolder={onUpdateFolder}
                    selectedBoardId={selectedBoardId}
                    treeDropIndicator={treeDropIndicator}
                  />
                </SidebarMenuSub>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </>
      ) : null}
    </SidebarMenuItem>
  );
}

function SpaceTree({
  activeTreeDrag,
  boardStatusActions,
  boards,
  expandedFolderIds,
  folders,
  mode,
  onArchiveBoard,
  onArchiveFolder,
  onCreateBoard,
  onCreateFolder,
  onSelectBoard,
  onToggleFolder,
  onUpdateBoard,
  onUpdateFolder,
  selectedBoardId,
  treeDropIndicator,
}: {
  activeTreeDrag: TreeOrderItem | null;
  boardStatusActions: BoardStatusActions;
  boards: BoardView[];
  expandedFolderIds: Set<string>;
  folders: FolderDefinition[];
  mode: MainMode;
  onArchiveBoard: (input: { boardId: string }) => Promise<void>;
  onArchiveFolder: (input: { folderId: string }) => Promise<void>;
  onCreateBoard: (spaceId: string, folderId: string | null, name: string, kind: BoardKind) => Promise<void>;
  onCreateFolder: (spaceId: string, parentFolderId: string | null, name: string) => Promise<void>;
  onSelectBoard: (board: BoardView) => void;
  onToggleFolder: (folderId: string) => void;
  onUpdateBoard: (input: { boardId: string; name: string }) => Promise<void>;
  onUpdateFolder: (input: { folderId: string; name: string }) => Promise<void>;
  selectedBoardId: string;
  treeDropIndicator: TreeDropIndicator | null;
}) {
  const t = useT();
  const visibleItems = treeItemsVisibleDuringDrag({
    activeTreeDrag,
    items: visibleTreeItems({ boards, expandedFolderIds, folders }),
    folders,
  });
  const visibleItemByKey = new Map(visibleItems.map((item) => [treeItemKey(item), item]));
  const hasChildren = visibleItems.length > 0;

  return (
    <div className="flex min-w-0 flex-col">
      <TreeBranch
        activeTreeDrag={activeTreeDrag}
        boardStatusActions={boardStatusActions}
        boards={boards}
        expandedFolderIds={expandedFolderIds}
        folders={folders}
        mode={mode}
        onArchiveBoard={onArchiveBoard}
        onArchiveFolder={onArchiveFolder}
        onCreateBoard={onCreateBoard}
        onCreateFolder={onCreateFolder}
        onSelectBoard={onSelectBoard}
        onToggleFolder={onToggleFolder}
        onUpdateBoard={onUpdateBoard}
        onUpdateFolder={onUpdateFolder}
        parentFolderId={null}
        selectedBoardId={selectedBoardId}
        treeDropIndicator={treeDropIndicator}
        visibleItemByKey={visibleItemByKey}
      />
      {hasChildren ? (
        <TreeTailDropZone
          isDropTarget={treeDropIndicator?.key === treeTailDropKey}
          isTreeDragging={activeTreeDrag !== null}
        />
      ) : (
        <SidebarMenuSubItem className="w-full">
          <p className="w-full px-2 py-1 text-xs text-sidebar-foreground/55">{t("space.emptyTree")}</p>
        </SidebarMenuSubItem>
      )}
    </div>
  );
}

// Catches drops in the empty space right below the tree so items can always
// be placed at the end of the root level.
function TreeTailDropZone({ isDropTarget, isTreeDragging }: { isDropTarget: boolean; isTreeDragging: boolean }) {
  const droppable = useDroppable({ data: { treeTail: true }, id: treeTailDropKey });

  return (
    <div className={cn("relative w-full", isTreeDragging ? "h-6" : "h-1")} ref={droppable.setNodeRef}>
      {isDropTarget ? <div className="pointer-events-none absolute inset-x-0 top-[2px] h-[3px] rounded-full bg-primary" /> : null}
    </div>
  );
}

function TreeBranch({
  activeTreeDrag,
  boardStatusActions,
  boards,
  expandedFolderIds,
  folders,
  mode,
  onArchiveBoard,
  onArchiveFolder,
  onCreateBoard,
  onCreateFolder,
  onSelectBoard,
  onToggleFolder,
  onUpdateBoard,
  onUpdateFolder,
  parentFolderId,
  selectedBoardId,
  treeDropIndicator,
  visibleItemByKey,
}: {
  activeTreeDrag: TreeOrderItem | null;
  boardStatusActions: BoardStatusActions;
  boards: BoardView[];
  expandedFolderIds: Set<string>;
  folders: FolderDefinition[];
  mode: MainMode;
  onArchiveBoard: (input: { boardId: string }) => Promise<void>;
  onArchiveFolder: (input: { folderId: string }) => Promise<void>;
  onCreateBoard: (spaceId: string, folderId: string | null, name: string, kind: BoardKind) => Promise<void>;
  onCreateFolder: (spaceId: string, parentFolderId: string | null, name: string) => Promise<void>;
  onSelectBoard: (board: BoardView) => void;
  onToggleFolder: (folderId: string) => void;
  onUpdateBoard: (input: { boardId: string; name: string }) => Promise<void>;
  onUpdateFolder: (input: { folderId: string; name: string }) => Promise<void>;
  parentFolderId: string | null;
  selectedBoardId: string;
  treeDropIndicator: TreeDropIndicator | null;
  visibleItemByKey: Map<string, TreeVisibleItem>;
}) {
  return treeChildren({ boards, folders, parentFolderId }).map((renderItem) => {
    const key = treeItemKey(renderItem);
    const item = visibleItemByKey.get(key);
    if (!item) {
      return null;
    }

    const nestedChildren =
      item.kind === "folder" && item.isExpanded ? (
        <SidebarMenuSub className={treeSubMenuClassName}>
          {item.hasChildren ? (
            <TreeBranch
              activeTreeDrag={activeTreeDrag}
              boardStatusActions={boardStatusActions}
              boards={boards}
              expandedFolderIds={expandedFolderIds}
              folders={folders}
              mode={mode}
              onArchiveBoard={onArchiveBoard}
              onArchiveFolder={onArchiveFolder}
              onCreateBoard={onCreateBoard}
              onCreateFolder={onCreateFolder}
              onSelectBoard={onSelectBoard}
              onToggleFolder={onToggleFolder}
              onUpdateBoard={onUpdateBoard}
              onUpdateFolder={onUpdateFolder}
              parentFolderId={item.id}
              selectedBoardId={selectedBoardId}
              treeDropIndicator={treeDropIndicator}
              visibleItemByKey={visibleItemByKey}
            />
          ) : (
            <SidebarMenuSubItem className="w-full">
              <TreeEmptyFolderDropZone
                folderId={item.id}
                isDropTarget={treeDropIndicator?.key === treeEmptyDropKey(item.id)}
                isTreeDragging={activeTreeDrag !== null}
              />
            </SidebarMenuSubItem>
          )}
        </SidebarMenuSub>
      ) : null;

    return (
      <TreeDndRow item={item} key={key}>
        {(dnd) => (
          <TreeVisibleRow
            board={item.kind === "board" ? requiredBoard(boards, item.id) : null}
            boardStatusActions={boardStatusActions}
            dnd={dnd}
            dropIndicator={treeDropIndicator?.key === key ? treeDropIndicator : null}
            folder={item.kind === "folder" ? requiredFolder(folders, item.id) : null}
            hasSprintBoard={boards.some((board) => board.kind === "sprint")}
            item={item}
            mode={mode}
            nestedChildren={nestedChildren}
            onArchiveBoard={onArchiveBoard}
            onArchiveFolder={onArchiveFolder}
            onCreateBoard={onCreateBoard}
            onCreateFolder={onCreateFolder}
            onSelectBoard={onSelectBoard}
            onToggleFolder={onToggleFolder}
            onUpdateBoard={onUpdateBoard}
            onUpdateFolder={onUpdateFolder}
            selectedBoardId={selectedBoardId}
          />
        )}
      </TreeDndRow>
    );
  });
}

function TreeDndRow({
  children,
  item,
}: {
  children: (dnd: TreeRowDndState) => ReactElement | null;
  item: TreeVisibleItem;
}) {
  const id = treeDraggableId(item);
  const draggable = useDraggable({ data: { treeItem: item }, id });
  const droppable = useDroppable({ data: { treeItem: item }, id: `drop:${id}` });

  return children({
    isDragging: draggable.isDragging,
    rowProps: { ...draggable.attributes, ...draggable.listeners },
    setRowRef: (element: HTMLElement | null) => {
      draggable.setNodeRef(element);
      droppable.setNodeRef(element);
    },
  });
}

function TreeDragClone({ item }: { item: TreeVisibleItem }) {
  return (
    <div
      className="pointer-events-none z-50 flex h-7 min-w-0 items-center gap-2 rounded-md bg-sidebar-accent/95 px-2 text-xs font-medium text-sidebar-accent-foreground shadow-lg ring-1 ring-sidebar-ring/60 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-sidebar-accent-foreground"
      style={treeDragCloneStyle(item.depth)}
    >
      {item.kind === "folder" ? <FolderIcon isExpanded={item.isExpanded} /> : <Table2 />}
      <span className="min-w-0 truncate">{item.name}</span>
    </div>
  );
}

function TreeEmptyFolderDropZone({
  folderId,
  isDropTarget,
  isTreeDragging,
}: {
  folderId: string;
  isDropTarget: boolean;
  isTreeDragging: boolean;
}) {
  const t = useT();
  const droppable = useDroppable({ data: { emptyFolderId: folderId }, id: treeEmptyDropKey(folderId) });

  return (
    <div
      className={cn(
        "flex h-7 w-full items-center rounded-md px-2 text-xs text-sidebar-foreground/45",
        isDropTarget && "bg-sidebar-accent text-sidebar-accent-foreground ring-1 ring-sidebar-ring/60",
      )}
      ref={droppable.setNodeRef}
    >
      {isTreeDragging ? t("board.dropHere") : t("board.emptyFolder")}
    </div>
  );
}

function TreeDropLine({ indicator }: { indicator: Extract<TreeDropIndicator, { kind: "line-after" | "line-before" }> }) {
  const offset = indicator.depthOffset * treeIndentPx;

  return (
    <div
      className={cn(
        "pointer-events-none absolute right-0 z-20 h-[3px] rounded-full bg-primary",
        indicator.kind === "line-before" ? "-top-[3.5px]" : "-bottom-[3.5px]",
      )}
      style={{ left: offset }}
    >
      <div className="absolute -left-1 -top-[2.5px] size-2 rounded-full border-2 border-primary bg-sidebar" />
    </div>
  );
}

function TreeVisibleRow({
  board,
  boardStatusActions,
  dnd,
  dropIndicator,
  folder,
  item,
  mode,
  nestedChildren,
  hasSprintBoard,
  onArchiveBoard,
  onArchiveFolder,
  onCreateBoard,
  onCreateFolder,
  onSelectBoard,
  onToggleFolder,
  onUpdateBoard,
  onUpdateFolder,
  selectedBoardId,
}: {
  board: BoardView | null;
  boardStatusActions: BoardStatusActions;
  dnd: TreeRowDndState;
  dropIndicator: TreeDropIndicator | null;
  folder: FolderDefinition | null;
  hasSprintBoard: boolean;
  item: TreeVisibleItem;
  mode: MainMode;
  nestedChildren: ReactNode;
  onArchiveBoard: (input: { boardId: string }) => Promise<void>;
  onArchiveFolder: (input: { folderId: string }) => Promise<void>;
  onCreateBoard: (spaceId: string, folderId: string | null, name: string, kind: BoardKind) => Promise<void>;
  onCreateFolder: (spaceId: string, parentFolderId: string | null, name: string) => Promise<void>;
  onSelectBoard: (board: BoardView) => void;
  onToggleFolder: (folderId: string) => void;
  onUpdateBoard: (input: { boardId: string; name: string }) => Promise<void>;
  onUpdateFolder: (input: { folderId: string; name: string }) => Promise<void>;
  selectedBoardId: string;
}) {
  const t = useT();
  const [isFolderPermissionsOpen, setIsFolderPermissionsOpen] = useState(false);
  const treeAccess = useContext(TreeAccessContext);
  if (item.kind === "folder" && folder) {
    return (
      <SidebarMenuSubItem className={cn("mb-1 w-full", dnd.isDragging && "opacity-45")}>
        <TreeActionMenu
          archiveDescription={`This folder, all nested folders, all boards inside it, and the folder content will be archived now and deleted after 30 days.`}
          archiveLabel="Archive folder"
          archiveTitle={`Archive "${folder.name}"?`}
          ariaLabel={t("common.optionsFor", { name: folder.name })}
          onArchive={() => onArchiveFolder({ folderId: folder.id })}
          onOpenPermissions={() => setIsFolderPermissionsOpen(true)}
        >
          {(optionsTrigger) => (
            <div
              className={cn(
                "group/tree-row relative flex h-7 w-full min-w-0 items-center gap-2 rounded-md px-2 text-xs text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-sidebar-accent-foreground",
                dropIndicator?.kind === "inside" && "bg-sidebar-accent text-sidebar-accent-foreground ring-1 ring-sidebar-ring/60",
              )}
              onClick={() => onToggleFolder(folder.id)}
              ref={dnd.setRowRef}
              {...dnd.rowProps}
            >
              {dropIndicator && dropIndicator.kind !== "inside" ? <TreeDropLine indicator={dropIndicator} /> : null}
              <button
                aria-label={item.isExpanded ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
                className="relative z-10 -ml-1 flex size-5 items-center justify-center rounded-sm hover:bg-sidebar-accent"
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleFolder(folder.id);
                }}
                onMouseDown={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
                type="button"
              >
                <ChevronRight className={cn("transition-transform", item.isExpanded && "rotate-90")} />
              </button>
              <FolderIcon isExpanded={item.isExpanded} />
              <EditableTreeLabel minLength={1} value={folder.name} onCommit={(name) => onUpdateFolder({ folderId: folder.id, name })} />
              <TreeAccessBadge scopeId={folder.id} scopeType="folder" />
              {optionsTrigger}
              <CreateTreeMenu
                align="end"
                ariaLabel={t("a11y.createIn", { name: folder.name })}
                hasSprintBoard={hasSprintBoard}
                onCreateBoard={(name, kind) => onCreateBoard(folder.spaceId, folder.id, name, kind)}
                onCreateFolder={(name) => onCreateFolder(folder.spaceId, folder.id, name)}
                trigger="inline"
              />
            </div>
          )}
        </TreeActionMenu>
        <ResourceAccessDialog
          name={folder.name}
          onOpenChange={(open) => {
            setIsFolderPermissionsOpen(open);
            if (!open) void treeAccess.reload();
          }}
          open={isFolderPermissionsOpen}
          scope={{ id: folder.id, type: "folder" }}
        />
        {nestedChildren}
      </SidebarMenuSubItem>
    );
  }

  if (item.kind === "board" && board) {
    return (
      <BoardTreeRow
        board={board}
        boardStatusActions={boardStatusActions}
        dnd={dnd}
        dropIndicator={dropIndicator}
        mode={mode}
        onArchiveBoard={onArchiveBoard}
        onSelectBoard={onSelectBoard}
        onUpdateBoard={onUpdateBoard}
        selectedBoardId={selectedBoardId}
      />
    );
  }

  return null;
}

function BoardTreeRow({
  board,
  boardStatusActions,
  dnd,
  dropIndicator,
  mode,
  onArchiveBoard,
  onSelectBoard,
  onUpdateBoard,
  selectedBoardId,
}: {
  board: BoardView;
  boardStatusActions: BoardStatusActions;
  dnd: TreeRowDndState;
  dropIndicator: TreeDropIndicator | null;
  mode: MainMode;
  onArchiveBoard: (input: { boardId: string }) => Promise<void>;
  onSelectBoard: (board: BoardView) => void;
  onUpdateBoard: (input: { boardId: string; name: string }) => Promise<void>;
  selectedBoardId: string;
}) {
  const t = useT();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);
  const [isStatusManagerOpen, setIsStatusManagerOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const treeAccess = useContext(TreeAccessContext);

  return (
    <SidebarMenuSubItem className="w-full">
      <BoardActionsMenu
        archiveTitle={`Archive "${board.name}"?`}
        ariaLabel={t("common.optionsFor", { name: board.name })}
        dropdownTrigger={
          <motion.button
            aria-label={t("common.optionsFor", { name: board.name })}
            className={cn(
              buttonVariants({ size: "icon-xs", variant: "ghost" }),
              "pointer-events-none ml-auto opacity-0 transition-opacity duration-100 ease-out aria-expanded:pointer-events-auto aria-expanded:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover/tree-row:pointer-events-auto group-hover/tree-row:opacity-100",
            )}
            initial={false}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            transition={actionMotionTransition}
            type="button"
          >
            <MoreHorizontal data-icon="inline-start" />
          </motion.button>
        }
        onArchive={() => onArchiveBoard({ boardId: board.id })}
        onOpenSettings={() => setIsSettingsOpen(true)}
        onOpenPermissions={() => setIsPermissionsOpen(true)}
        onOpenStatuses={() => setIsStatusManagerOpen(true)}
        onOpenTemplates={() => setIsTemplateDialogOpen(true)}
      >
        {(optionsTrigger) => (
          <div
            className={cn(
              "group/tree-row relative mb-1 flex h-7 w-full min-w-0 -translate-x-px items-center gap-2 rounded-md px-2 text-xs text-sidebar-foreground ring-sidebar-ring outline-hidden transition-opacity group-data-[collapsible=icon]:hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-sidebar-accent-foreground",
              board.id === selectedBoardId && mode === "board" && "bg-sidebar-accent text-sidebar-accent-foreground",
              dnd.isDragging && "opacity-45",
            )}
            data-active={board.id === selectedBoardId && mode === "board"}
            ref={dnd.setRowRef}
            {...dnd.rowProps}
          >
            {dropIndicator && dropIndicator.kind !== "inside" ? <TreeDropLine indicator={dropIndicator} /> : null}
            <button className="relative z-10 flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => onSelectBoard(board)} type="button">
              {board.kind === "sprint" ? <Zap /> : <Table2 />}
              <EditableTreeLabel minLength={1} value={board.name} onCommit={(name) => onUpdateBoard({ boardId: board.id, name })} />
              <TreeAccessBadge scopeId={board.id} scopeType="board" />
            </button>
            {optionsTrigger}
          </div>
        )}
      </BoardActionsMenu>
      <BoardSettingsDialog
        board={board}
        disabled={false}
        onArchive={() => onArchiveBoard({ boardId: board.id })}
        onOpenChange={setIsSettingsOpen}
        open={isSettingsOpen}
      />
      <ResourceAccessDialog
        name={board.name}
        onOpenChange={(open) => {
          setIsPermissionsOpen(open);
          if (!open) void treeAccess.reload();
        }}
        open={isPermissionsOpen}
        scope={{ id: board.id, type: "board" }}
      />
      <ApplyBoardTemplateDialog
        boardId={board.id}
        onApplied={() => treeAccess.reload()}
        onOpenChange={setIsTemplateDialogOpen}
        open={isTemplateDialogOpen}
      />
      <BoardStatusManagerDialog
        board={board}
        disabled={false}
        onArchiveStatus={boardStatusActions.archiveStatus}
        onClearStatusEntryRule={boardStatusActions.clearStatusEntryRule}
        onCreateStatus={boardStatusActions.createStatus}
        onMoveStatus={boardStatusActions.moveStatus}
        onOpenChange={setIsStatusManagerOpen}
        onSetDefaultStatus={boardStatusActions.setDefaultStatus}
        onUpdateStatus={boardStatusActions.updateStatus}
        onUpdateStatusEntryRule={boardStatusActions.updateStatusEntryRule}
        open={isStatusManagerOpen}
      />
    </SidebarMenuSubItem>
  );
}

function CreateTreeMenu({
  align = "start",
  ariaLabel,
  hasSprintBoard,
  onCreateBoard,
  onCreateFolder,
  trigger,
}: {
  align?: "center" | "end" | "start";
  ariaLabel: string;
  hasSprintBoard: boolean;
  onCreateBoard: (name: string, kind: BoardKind) => Promise<void>;
  onCreateFolder: (name: string) => Promise<void>;
  trigger: "inline" | "sidebar-action";
}) {
  const t = useT();
  const [createKind, setCreateKind] = useState<"board" | "folder" | "sprint" | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const triggerButton =
    trigger === "sidebar-action" ? (
      <SidebarMenuAction
        render={
          <motion.button
            aria-label={ariaLabel}
            className={cn(
              "pointer-events-none opacity-0 transition-opacity duration-100 ease-out focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover/menu-item:pointer-events-auto group-hover/menu-item:opacity-100",
              isOpen && "pointer-events-auto opacity-100",
            )}
            initial={false}
            onClick={(event) => event.stopPropagation()}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.94 }}
            transition={actionMotionTransition}
            type="button"
          />
        }
      >
        <Plus />
      </SidebarMenuAction>
    ) : (
      <motion.button
        aria-label={ariaLabel}
        className={cn(
          buttonVariants({ size: "icon-xs", variant: "ghost" }),
          "pointer-events-none ml-auto opacity-0 transition-opacity duration-100 ease-out focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover/tree-row:pointer-events-auto group-hover/tree-row:opacity-100",
          isOpen && "pointer-events-auto opacity-100",
        )}
        initial={false}
        onClick={(event) => event.stopPropagation()}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        transition={actionMotionTransition}
        type="button"
      >
        <Plus data-icon="inline-start" />
      </motion.button>
    );

  function resetCreateMenu() {
    setCreateKind(null);
    setCreateError(null);
    setName("");
  }

  async function submitCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextName = name.trim().replace(/\s+/g, " ");
    setCreateError(null);
    if (!createKind || nextName.length < 1) {
      return;
    }
    setIsCreating(true);
    try {
      if (createKind === "folder") {
        await onCreateFolder(nextName);
      } else {
        await onCreateBoard(nextName, createKind === "sprint" ? "sprint" : "standard");
      }
      resetCreateMenu();
      setIsOpen(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Could not create item.");
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <DropdownMenu
      onOpenChange={(nextOpen) => {
        setIsOpen(nextOpen);
        if (!nextOpen) {
          resetCreateMenu();
        }
      }}
      open={isOpen}
    >
      <DropdownMenuTrigger render={triggerButton} />
      <DropdownMenuContent align={align} className="w-36">
        <DropdownMenuGroup>
          {createKind ? (
            <form className="flex flex-col gap-2 p-1" onSubmit={submitCreate}>
              <Input
                autoFocus
                className="h-7"
                disabled={isCreating}
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
                placeholder={
                  createKind === "folder"
                    ? t("board.folderName")
                    : createKind === "sprint"
                      ? t("board.sprintBoardName")
                      : t("board.boardName")
                }
                value={name}
              />
              {createError ? <p className="px-1 text-xs text-destructive">{createError}</p> : null}
              <div className="flex justify-end gap-1">
                <Button
                  onClick={() => {
                    resetCreateMenu();
                    setIsOpen(false);
                  }}
                  size="xs"
                  type="button"
                  variant="ghost"
                >
                  {t("common.cancel")}
                </Button>
                <Button disabled={isCreating || name.trim().length < 1} size="xs" type="submit">
                  {t("common.create")}
                </Button>
              </div>
            </form>
          ) : (
            <>
              <DropdownMenuItem
                closeOnClick={false}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setCreateError(null);
                  setCreateKind("folder");
                }}
              >
                <Folder data-icon="inline-start" />
                {t("space.folder")}
              </DropdownMenuItem>
              <DropdownMenuItem
                closeOnClick={false}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setCreateError(null);
                  setCreateKind("board");
                }}
              >
                <Table2 data-icon="inline-start" />
                {t("space.board")}
              </DropdownMenuItem>
              {hasSprintBoard ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <span className="block w-full">
                        <DropdownMenuItem disabled>
                          <Zap data-icon="inline-start" />
                          {t("space.sprintBoard")}
                        </DropdownMenuItem>
                      </span>
                    }
                  />
                  <TooltipContent side="right">{t("space.sprintBoardExists")}</TooltipContent>
                </Tooltip>
              ) : (
                <DropdownMenuItem
                  closeOnClick={false}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setCreateError(null);
                    setCreateKind("sprint");
                  }}
                >
                  <Zap data-icon="inline-start" />
                  {t("space.sprintBoard")}
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type TreeAction = {
  disabled?: boolean;
  icon?: ReactNode;
  id: string;
  label: string;
  onSelect?: () => void;
  shortcut?: MenuShortcut;
  variant?: "default" | "destructive";
};

function TreeActionMenu({
  archiveDescription,
  archiveLabel,
  archiveTitle,
  ariaLabel,
  children,
  onArchive,
  onOpenPermissions,
}: {
  archiveDescription: string;
  archiveLabel: string;
  archiveTitle: string;
  ariaLabel: string;
  children: (optionsTrigger: ReactNode) => ReactElement;
  onArchive: () => Promise<void>;
  onOpenPermissions: () => void;
}) {
  const t = useT();
  const [isContextOpen, setIsContextOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const menuActions: TreeAction[] = [
    { disabled: true, id: "settings", label: t("space.settings"), shortcut: { shortcut: "Mod+," } },
    { id: "permissions", label: t("board.accessShare"), onSelect: openPermissions, shortcut: { shortcut: "Mod+Shift+P" } },
    {
      icon: <Archive data-icon="inline-start" />,
      id: "archive",
      label: archiveLabel,
      onSelect: openArchiveDialog,
      shortcut: { linux: "Ctrl+Backspace", macos: "Mod+Backspace", shortcut: "Mod+Backspace", windows: "Ctrl+Backspace" },
      variant: "destructive",
    },
  ];

  async function archive() {
    setIsArchiving(true);
    try {
      await onArchive();
      setIsArchiveOpen(false);
      setIsContextOpen(false);
      setIsDropdownOpen(false);
    } finally {
      setIsArchiving(false);
    }
  }

  function openArchiveDialog() {
    setIsContextOpen(false);
    setIsDropdownOpen(false);
    window.setTimeout(() => setIsArchiveOpen(true), 0);
  }

  function openPermissions() {
    setIsContextOpen(false);
    setIsDropdownOpen(false);
    window.setTimeout(onOpenPermissions, 0);
  }

  function handleMenuKeyDown(event: React.KeyboardEvent) {
    for (const action of menuActions) {
      if (action.disabled || !action.onSelect || !matchesMenuShortcut(event, action.shortcut)) {
        continue;
      }
      event.preventDefault();
      event.stopPropagation();
      action.onSelect();
      return;
    }
  }

  const optionsTrigger = (
    <DropdownMenu modal={false} onOpenChange={setIsDropdownOpen} open={isDropdownOpen}>
      <DropdownMenuTrigger
        render={
          <motion.button
            aria-label={ariaLabel}
            className={cn(
              buttonVariants({ size: "icon-xs", variant: "ghost" }),
              "pointer-events-none ml-auto opacity-0 transition-opacity duration-100 ease-out focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover/tree-row:pointer-events-auto group-hover/tree-row:opacity-100",
              (isDropdownOpen || isContextOpen || isArchiveOpen) && "pointer-events-auto opacity-100",
            )}
            initial={false}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            transition={actionMotionTransition}
            type="button"
          />
        }
      >
        <MoreHorizontal data-icon="inline-start" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" onClick={(event) => event.stopPropagation()} onKeyDown={handleMenuKeyDown}>
        <DropdownMenuGroup>{menuActions.map((action) => renderDropdownTreeAction(action))}</DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <AlertDialog onOpenChange={setIsArchiveOpen} open={isArchiveOpen}>
      <ContextMenu onOpenChange={setIsContextOpen}>
        <ContextMenuTrigger render={children(optionsTrigger)} />
        <ContextMenuContent className="w-52" onClick={(event) => event.stopPropagation()} onKeyDown={handleMenuKeyDown}>
          <ContextMenuGroup>{menuActions.map((action) => renderContextTreeAction(action))}</ContextMenuGroup>
        </ContextMenuContent>
      </ContextMenu>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{archiveTitle}</AlertDialogTitle>
          <AlertDialogDescription>{archiveDescription}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isArchiving}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction disabled={isArchiving} onClick={() => void archive()} variant="destructive">
            {isArchiving ? t("board.archiving") : t("board.archive")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function renderDropdownTreeAction(action: TreeAction) {
  const shortcut = formatMenuShortcut(action.shortcut);

  return (
    <DropdownMenuItem
      disabled={action.disabled}
      key={action.id}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        action.onSelect?.();
      }}
      variant={action.variant}
    >
      {action.icon}
      {action.label}
      {shortcut ? <DropdownMenuShortcut>{shortcut}</DropdownMenuShortcut> : null}
    </DropdownMenuItem>
  );
}

function renderContextTreeAction(action: TreeAction) {
  const shortcut = formatMenuShortcut(action.shortcut);

  return (
    <ContextMenuItem
      disabled={action.disabled}
      key={action.id}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        action.onSelect?.();
      }}
      variant={action.variant}
    >
      {action.icon}
      {action.label}
      {shortcut ? <ContextMenuShortcut>{shortcut}</ContextMenuShortcut> : null}
    </ContextMenuItem>
  );
}

function EditableTreeLabel({
  className,
  minLength = 2,
  onCommit,
  value,
}: {
  className?: string;
  minLength?: number;
  onCommit: (value: string) => Promise<void> | void;
  value: string;
}) {
  const t = useT();
  const [draft, setDraft] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setDraft(value);
    }
  }, [isEditing, value]);

  async function commit() {
    const nextValue = draft.trim().replace(/\s+/g, " ");
    if (nextValue.length < minLength || nextValue === value) {
      setDraft(value);
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onCommit(nextValue);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }

  if (isEditing) {
    return (
      <Input
        autoFocus
        className={cn("h-5 min-w-0 flex-1 px-1 py-0 text-xs", className)}
        disabled={isSaving}
        onBlur={() => void commit()}
        onChange={(event) => setDraft(event.target.value)}
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Enter") {
            event.preventDefault();
            void commit();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            setDraft(value);
            setIsEditing(false);
          }
        }}
        value={draft}
      />
    );
  }

  return (
    <span
      className={cn("min-w-0 flex-1 truncate", className)}
      onDoubleClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsEditing(true);
      }}
      title={value}
    >
      {value}
    </span>
  );
}

function hasSelectedBoardDescendant(
  folderId: string,
  boards: BoardView[],
  folders: FolderDefinition[],
  selectedBoardId: string,
): boolean {
  if (boards.some((board) => board.folderId === folderId && board.id === selectedBoardId)) {
    return true;
  }

  return folders
    .filter((folder) => folder.parentFolderId === folderId)
    .some((folder) => hasSelectedBoardDescendant(folder.id, boards, folders, selectedBoardId));
}

function collectFolderSubtreeIds(folders: FolderDefinition[], folderId: string) {
  const ids = new Set<string>([folderId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const folder of folders) {
      if (folder.parentFolderId && ids.has(folder.parentFolderId) && !ids.has(folder.id)) {
        ids.add(folder.id);
        changed = true;
      }
    }
  }
  return ids;
}

function treeChildren({
  boards,
  folders,
  parentFolderId,
}: {
  boards: BoardView[];
  folders: FolderDefinition[];
  parentFolderId: string | null;
}): TreeRenderItem[] {
  return [
    ...folders
      .filter((folder) => (folder.parentFolderId ?? null) === parentFolderId)
      .map((folder) => ({
        id: folder.id,
        kind: "folder" as const,
        name: folder.name,
        parentFolderId: folder.parentFolderId ?? null,
        rank: folder.rank,
        spaceId: folder.spaceId,
      })),
    ...boards
      .filter((board) => (board.folderId ?? null) === parentFolderId)
      .map((board) => ({
        id: board.id,
        isDefault: board.purpose === "default",
        kind: "board" as const,
        name: board.name,
        parentFolderId: board.folderId ?? null,
        rank: board.rank,
        spaceId: board.spaceId,
      })),
  ].sort(compareTreeItems);
}

function visibleTreeItems({
  boards,
  expandedFolderIds,
  folders,
  parentFolderId = null,
  depth = 0,
}: {
  boards: BoardView[];
  expandedFolderIds: Set<string>;
  folders: FolderDefinition[];
  parentFolderId?: string | null;
  depth?: number;
}): TreeVisibleItem[] {
  return treeChildren({ boards, folders, parentFolderId }).flatMap((item) => {
    const childItems = item.kind === "folder" ? treeChildren({ boards, folders, parentFolderId: item.id }) : [];
    const isExpanded = item.kind === "folder" && expandedFolderIds.has(item.id);
    const visibleItem: TreeVisibleItem = {
      ...item,
      depth,
      hasChildren: childItems.length > 0,
      isExpanded,
    };
    if (item.kind !== "folder" || !isExpanded) {
      return [visibleItem];
    }
    return [
      visibleItem,
      ...visibleTreeItems({
        boards,
        expandedFolderIds,
        folders,
        parentFolderId: item.id,
        depth: depth + 1,
      }),
    ];
  });
}

function treeItemsVisibleDuringDrag({
  activeTreeDrag,
  folders,
  items,
}: {
  activeTreeDrag: TreeOrderItem | null;
  folders: FolderDefinition[];
  items: TreeVisibleItem[];
}) {
  if (activeTreeDrag?.kind !== "folder") {
    return items;
  }

  const subtreeIds = collectFolderSubtreeIds(folders, activeTreeDrag.id);
  return items.filter((item) => {
    if (item.kind === "folder") {
      return item.id === activeTreeDrag.id || !subtreeIds.has(item.parentFolderId ?? "");
    }
    return !item.parentFolderId || !subtreeIds.has(item.parentFolderId);
  });
}

function compareTreeItems(a: TreeRenderItem, b: TreeRenderItem) {
  const defaultDiff = Number(Boolean(b.isDefault)) - Number(Boolean(a.isDefault));
  if (defaultDiff !== 0) {
    return defaultDiff;
  }
  const rankDiff = compareOrderKeys(a.rank, b.rank);
  if (rankDiff !== 0) {
    return rankDiff;
  }
  return a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
}

function compareOrderKeys(first: string, second: string) {
  if (first < second) {
    return -1;
  }
  if (first > second) {
    return 1;
  }
  return 0;
}

function nextTreeOrder({
  active,
  boards,
  folders,
  over,
}: {
  active: TreeOrderItem;
  boards: BoardView[];
  folders: FolderDefinition[];
  over: TreeDropTarget;
}) {
  const parentFolderId = over.parentFolderId;
  const siblings = treeChildren({ boards, folders, parentFolderId }).filter(
    (item) => !(item.kind === active.kind && item.id === active.id),
  );
  const activeItem = treeItemById({ boards, folders, item: active });
  const nextItem = { ...activeItem, parentFolderId };
  if (over.kind === "item") {
    const overIndex = siblings.findIndex((item) => item.kind === over.item.kind && item.id === over.item.id);
    if (overIndex >= 0) {
      siblings.splice(over.placement === "after" ? overIndex + 1 : overIndex, 0, nextItem);
      return siblings.map(({ id, kind }) => ({ id, kind }));
    }
  }
  return [...siblings, nextItem].map(({ id, kind }) => ({ id, kind }));
}

function isSameTreeItem(target: TreeDropTarget, active: TreeOrderItem) {
  return target.kind === "item" && target.item.kind === active.kind && target.item.id === active.id;
}

function treeItemById({
  boards,
  folders,
  item,
}: {
  boards: BoardView[];
  folders: FolderDefinition[];
  item: TreeOrderItem;
}): TreeRenderItem {
  return item.kind === "board"
    ? {
        id: requiredBoard(boards, item.id).id,
        isDefault: requiredBoard(boards, item.id).purpose === "default",
        kind: "board",
        name: requiredBoard(boards, item.id).name,
        parentFolderId: requiredBoard(boards, item.id).folderId ?? null,
        rank: requiredBoard(boards, item.id).rank,
        spaceId: requiredBoard(boards, item.id).spaceId,
      }
    : {
        id: requiredFolder(folders, item.id).id,
        kind: "folder",
        name: requiredFolder(folders, item.id).name,
        parentFolderId: requiredFolder(folders, item.id).parentFolderId ?? null,
        rank: requiredFolder(folders, item.id).rank,
        spaceId: requiredFolder(folders, item.id).spaceId,
      };
}

function treeVisibleItemById({
  boards,
  expandedFolderIds,
  folders,
  item,
}: {
  boards: BoardView[];
  expandedFolderIds: Set<string>;
  folders: FolderDefinition[];
  item: TreeOrderItem;
}) {
  const visibleItem = visibleTreeItems({ boards, expandedFolderIds, folders }).find(
    (candidate) => candidate.kind === item.kind && candidate.id === item.id,
  );
  if (visibleItem) {
    return visibleItem;
  }

  const renderItem =
    item.kind === "board"
      ? boards.find((board) => board.id === item.id)
        ? treeItemById({ boards, folders, item })
        : null
      : folders.find((folder) => folder.id === item.id)
        ? treeItemById({ boards, folders, item })
        : null;
  if (!renderItem) {
    return null;
  }

  const childItems = renderItem.kind === "folder" ? treeChildren({ boards, folders, parentFolderId: renderItem.id }) : [];
  return {
    ...renderItem,
    depth: 0,
    hasChildren: childItems.length > 0,
    isExpanded: renderItem.kind === "folder" && expandedFolderIds.has(renderItem.id),
  };
}

function treeDragCloneStyle(depth: number) {
  const width = treeBaseRowWidthRem - depth * treeNestedRowInsetRem;
  return { width: `${Math.max(width, 8)}rem` };
}

function requiredBoard(boards: BoardView[], boardId: string) {
  const board = boards.find((item) => item.id === boardId);
  if (!board) {
    throw new Error("Board was not found in tree");
  }
  return board;
}

function requiredFolder(folders: FolderDefinition[], folderId: string) {
  const folder = folders.find((item) => item.id === folderId);
  if (!folder) {
    throw new Error("Folder was not found in tree");
  }
  return folder;
}

function FolderIcon({ isExpanded }: { isExpanded: boolean }) {
  const Icon = isExpanded ? FolderOpen : Folder;

  return (
    <span className="flex size-5 items-center justify-center rounded-sm">
      <Icon />
    </span>
  );
}

function treeDraggableId(item: TreeOrderItem) {
  return `${item.kind}:${item.id}`;
}

function treeItemKey(item: TreeOrderItem) {
  return `${item.kind}:${item.id}`;
}

function treeEmptyDropKey(folderId: string) {
  return `empty:${folderId}`;
}

const treeTailDropKey = "tree-tail";

// Rows are matched by the pointer's vertical position only; the horizontal
// position is left unbounded on the left so the pointer can slide out to pick
// a shallower nesting depth at subtree boundaries. Each row's hit area is
// stretched by half the row gap so the space between rows always resolves to
// the nearest row edge instead of depending on the drag direction.
const treeRowGapPx = 4;

const treeCollisionDetection: CollisionDetection = ({ droppableContainers, droppableRects, pointerCoordinates }) => {
  if (!pointerCoordinates) {
    return [];
  }
  return droppableContainers
    .flatMap((container) => {
      const rect = droppableRects.get(container.id);
      if (!rect) {
        return [];
      }
      if (
        pointerCoordinates.y < rect.top - treeRowGapPx / 2 ||
        pointerCoordinates.y > rect.bottom + treeRowGapPx / 2 ||
        pointerCoordinates.x > rect.right
      ) {
        return [];
      }
      return [{ data: { droppableContainer: container, value: rect.left }, id: container.id }];
    })
    .sort((first, second) => (second.data.value as number) - (first.data.value as number));
};

function treeItemFromDndData(data: Record<string, unknown> | undefined): TreeVisibleItem | null {
  const item = data?.treeItem;
  return item ? (item as TreeVisibleItem) : null;
}

function isSameTreeResolution(first: TreeDropResolution | null, second: TreeDropResolution | null) {
  if (first === null || second === null) {
    return first === second;
  }
  return (
    first.indicator.key === second.indicator.key &&
    first.indicator.kind === second.indicator.kind &&
    (first.indicator.kind === "inside" || second.indicator.kind === "inside"
      ? true
      : first.indicator.depthOffset === second.indicator.depthOffset) &&
    JSON.stringify(first.target) === JSON.stringify(second.target)
  );
}

function treeAncestorAtDepth(items: TreeVisibleItem[], fromIndex: number, depth: number) {
  for (let index = fromIndex; index >= 0; index -= 1) {
    const candidate = items[index];
    if (!candidate) {
      return null;
    }
    if (candidate.depth === depth) {
      return candidate;
    }
    if (candidate.depth < depth) {
      return null;
    }
  }
  return null;
}

// Translates the pointer position over a row into an explicit drop target.
// The top band inserts before the row and the bottom band inserts after it at
// the row's own level — nesting into a folder happens only through the middle
// band of the folder row (highlighted), never by accident. In the bottom band
// the pointer can slide left past the row's indent to close nesting levels
// and insert after an ancestor instead.
function resolveTreeDrop({
  active,
  overData,
  overRect,
  pointer,
  visibleItems,
}: {
  active: TreeVisibleItem;
  overData: Record<string, unknown>;
  overRect: { height: number; left: number; top: number };
  pointer: { x: number; y: number };
  visibleItems: TreeVisibleItem[];
}): TreeDropResolution | null {
  const spaceId = active.spaceId;
  const emptyFolderId = typeof overData.emptyFolderId === "string" ? overData.emptyFolderId : null;
  if (emptyFolderId) {
    return {
      indicator: { key: treeEmptyDropKey(emptyFolderId), kind: "inside" },
      target: { kind: "container", parentFolderId: emptyFolderId, spaceId },
    };
  }

  if (overData.treeTail === true) {
    return {
      indicator: { key: treeTailDropKey, kind: "inside" },
      target: { kind: "container", parentFolderId: null, spaceId },
    };
  }

  const item = treeItemFromDndData(overData);
  if (!item || (item.kind === active.kind && item.id === active.id)) {
    return null;
  }

  const key = treeItemKey(item);
  const isFolder = item.kind === "folder";
  const ratio = (pointer.y - overRect.top) / Math.max(overRect.height, 1);

  if (ratio < (isFolder ? 0.35 : 0.5)) {
    return {
      indicator: { depthOffset: 0, key, kind: "line-before" },
      target: { item: { id: item.id, kind: item.kind }, kind: "item", parentFolderId: item.parentFolderId, placement: "before", spaceId },
    };
  }

  if (isFolder && ratio < 0.65) {
    return {
      indicator: { key, kind: "inside" },
      target: { kind: "container", parentFolderId: item.id, spaceId },
    };
  }

  const index = visibleItems.findIndex((candidate) => treeItemKey(candidate) === key);
  const nextItem = index >= 0 ? visibleItems[index + 1] ?? null : null;
  const minDepth = nextItem ? nextItem.depth : 0;
  const shallowSteps = pointer.x < overRect.left ? Math.ceil((overRect.left - pointer.x) / treeIndentPx) : 0;
  const depth = Math.max(minDepth, item.depth - shallowSteps);

  // The gap below an expanded folder with children belongs to its subtree, so
  // the only valid insertion there is before the first child.
  if (depth === item.depth + 1 && isFolder && item.isExpanded && nextItem && nextItem.parentFolderId === item.id) {
    return {
      indicator: { depthOffset: 1, key, kind: "line-after" },
      target: { item: { id: nextItem.id, kind: nextItem.kind }, kind: "item", parentFolderId: item.id, placement: "before", spaceId },
    };
  }

  if (depth >= item.depth) {
    return {
      indicator: { depthOffset: 0, key, kind: "line-after" },
      target: { item: { id: item.id, kind: item.kind }, kind: "item", parentFolderId: item.parentFolderId, placement: "after", spaceId },
    };
  }

  const ancestor = index >= 0 ? treeAncestorAtDepth(visibleItems, index, depth) : null;
  if (!ancestor) {
    return null;
  }
  return {
    indicator: { depthOffset: depth - item.depth, key, kind: "line-after" },
    target: { item: { id: ancestor.id, kind: ancestor.kind }, kind: "item", parentFolderId: ancestor.parentFolderId, placement: "after", spaceId },
  };
}

function WorkspaceMark({ className, name }: { className?: string; name: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 select-none items-center justify-center bg-primary/15 font-semibold text-primary",
        className,
      )}
    >
      {(name.trim().charAt(0) || "W").toUpperCase()}
    </span>
  );
}

function WorkspaceModeItem({
  active,
  icon: Icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: typeof Layers3;
  label: string;
  onClick: () => void;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton isActive={active} onClick={onClick} size="sm">
        <Icon />
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function CreateWorkspaceDialog({
  onCreate,
  onOpenChange,
  open,
}: {
  onCreate: (name: string) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  const t = useT();
  const [name, setName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsCreating(true);
    try {
      // onCreate ends with a full page reload, so the dialog never needs to
      // reset its own state on success.
      await onCreate(name);
    } catch (err) {
      setIsCreating(false);
      throw err;
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("nav.createWorkspace")}</DialogTitle>
          <DialogDescription>
            A workspace has its own members, types, fields, and boards. You become its owner and switch to it right away.
          </DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4 px-6 pb-6" onSubmit={submit}>
          <label className="flex flex-col gap-2 text-sm font-medium">
            {t("space.name")}
            <Input onChange={(event) => setName(event.target.value)} placeholder={t("space.namePlaceholder")} value={name} />
          </label>
          <div className="flex justify-end gap-2">
            <Button onClick={() => onOpenChange(false)} type="button" variant="outline">
              {t("common.cancel")}
            </Button>
            <Button disabled={isCreating || name.trim().length < 2} type="submit">
              <Plus data-icon="inline-start" />
              {t("nav.createWorkspace")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateSpaceDialog({
  disabled,
  onCreate,
  onOpenChange,
  open,
}: {
  disabled: boolean;
  onCreate: (input: { description: string; name: string }) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  const t = useT();
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onCreate({ description, name });
    setDescription("");
    setName("");
    onOpenChange(false);
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("nav.createSpace")}</DialogTitle>
          <DialogDescription>{t("space.whatItIsShort")}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4 px-6 pb-6" onSubmit={submit}>
          <label className="flex flex-col gap-2 text-sm font-medium">
            {t("space.name")}
            <Input onChange={(event) => setName(event.target.value)} placeholder={t("space.namePlaceholder")} value={name} />
          </label>
          <label className="flex flex-col gap-2 text-sm font-medium">
            {t("space.description")}
            <Input onChange={(event) => setDescription(event.target.value)} placeholder={t("space.descriptionPlaceholder")} value={description} />
          </label>
          <div className="flex justify-end gap-2">
            <Button onClick={() => onOpenChange(false)} type="button" variant="outline">
              {t("common.cancel")}
            </Button>
            <Button disabled={disabled || name.trim().length < 2} type="submit">
              <Plus data-icon="inline-start" />
              {t("nav.createSpace")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function mainModeTitle(mode: MainMode, t: Translate) {
  if (mode === "types") return t("nav.types");
  if (mode === "fields") return t("nav.fields");
  if (mode === "team") return t("nav.team");
  if (mode === "integrations") return t("nav.integrations");
  if (mode === "activity") return t("nav.activity");
  if (mode === "trash") return t("nav.trash");
  if (mode === "my-work") return t("nav.myWork");
  return t("space.board");
}

function parseDashboardRoute(pathname: string): DashboardRoute {
  const parts = pathname.split("/").filter(Boolean).map(decodeURIComponent);
  const [root, spaceId, boardsRoot, boardId, viewsRoot, viewId] = parts;

  if (
    root === "types" ||
    root === "fields" ||
    root === "team" ||
    root === "my-work" ||
    root === "trash" ||
    root === "integrations" ||
    root === "activity"
  ) {
    return { boardId: "", mode: root, spaceId: "", viewId: "" };
  }

  if (root === "spaces") {
    const hasBoard = boardsRoot === "boards";
    return {
      boardId: hasBoard ? boardId ?? "" : "",
      mode: "board",
      spaceId: spaceId ?? "",
      viewId: hasBoard && viewsRoot === "views" ? viewId ?? "" : "",
    };
  }

  return { boardId: "", mode: "board", spaceId: "", viewId: "" };
}

function dashboardPath(route: DashboardRoute) {
  if (route.mode !== "board") {
    return `/${route.mode}`;
  }

  if (route.spaceId && route.boardId && route.viewId) {
    return `/spaces/${encodeURIComponent(route.spaceId)}/boards/${encodeURIComponent(route.boardId)}/views/${encodeURIComponent(route.viewId)}`;
  }

  if (route.spaceId && route.boardId) {
    return `/spaces/${encodeURIComponent(route.spaceId)}/boards/${encodeURIComponent(route.boardId)}`;
  }

  if (route.spaceId) {
    return `/spaces/${encodeURIComponent(route.spaceId)}`;
  }

  return "/";
}

function normalizeDashboardRoute(
  route: DashboardRoute,
  selection: {
    allBoards: BoardView[];
    boardsForSelectedSpace: BoardView[];
    selectedBoard: BoardView | null;
    selectedSpace: SpaceDefinition | null;
  },
): DashboardRoute {
  if (route.mode !== "board") {
    return { boardId: "", mode: route.mode, spaceId: "", viewId: "" };
  }

  if (selection.selectedBoard) {
    const views = selection.selectedBoard.views;
    const selectedView =
      views.find((view) => view.id === route.viewId) ??
      views.find((view) => view.isDefault) ??
      views[0] ??
      null;
    return {
      boardId: selection.selectedBoard.id,
      mode: "board",
      spaceId: selection.selectedBoard.spaceId,
      viewId: selectedView?.id ?? "",
    };
  }

  if (selection.selectedSpace) {
    return { boardId: "", mode: "board", spaceId: selection.selectedSpace.id, viewId: "" };
  }

  const fallbackBoard = selection.allBoards[0] ?? null;
  if (fallbackBoard) {
    return { boardId: fallbackBoard.id, mode: "board", spaceId: fallbackBoard.spaceId, viewId: "" };
  }

  return { boardId: "", mode: "board", spaceId: "", viewId: "" };
}
